
import React, { useState, useEffect } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import styles from './form.module.scss';
import numToWords from '../../helpr/numToWords';

import informationIcon from '../../assets/information.svg';
import { dateLessThan } from '../../helpr/generic-validation-rules';

/*
  Generic form group component for grouping fields.
  chilren prop for FormGroup content
  header for form group header title
*/
export const FormGroup = ({ children, header }) => {
  return (
    <div className={styles['form-group']}>
      <div className={styles['form-group-header']}>
        { header }
      </div>
      { children }
    </div>
  )
}

/*
  Generic Input group component for grouping fields without a separate card.
  chilren prop for FormGroup content
  header for form group header title
*/
export const InputGroup = ({ children, header, style }) => {
  return (
    <div className={styles['input-group']} style={style}>
      <div className={styles['input-group-header']}>
        { header }
      </div>
      { children }
    </div>
  )
}

/*
  Generic Field group component for grouping fields.
  No card formatting
*/
export const FieldGroup = ({ children, header, style }) => {
  return (
    <div className={styles['field-group']} style={style}>
      <div className={styles['field-group-header']}>
        { header }
      </div>
      { children }
    </div>
  )
}

/*
  Renders dropdown input with dynamic props,
  inputProps prop for dynamic input property
  values propfor dropdown selection
  noDefault prop for specifying if the drop has no default value
*/
export const DropDown = ({children, inputProps, values, noDefault=true, disabled, selectName="Select"})  => {
  return (
    <div className={styles['form-field']}>
      <div className={styles['form-field-label']}>
        { children }
      </div>
      <div className={styles['form-field-input']}>
        <select { ...inputProps } disabled={disabled}>
          <option value="" disabled selected={!noDefault}>{selectName}</option>
          { values.map((data,index)=>{ return <option key={index} value={data['value']}>{data['name']}</option>}) }
        </select>
      </div>
    </div>
  )
}

/*
  Renders Text Input Field with input props.
  inputProps for validation and value binding
*/
export const TextInput = ({children, inputProps, maxLength, placeholder, pattern=null, disabled}) => {
  const { name, onChange, style, value } = inputProps;
  const [ inputValue, setInputValue ] = useState(value);
  useEffect(()=>{setInputValue(value)},[value]);
  const setInputBuffer = (string) => {
    if(string && pattern){
      if(pattern.test(string)){setInputValue(string)};
    } else {
      setInputValue(string);
    }
  }
  return (
    <div className={styles['form-field']}>
      <div className={styles['form-field-label']}>
        { children }
      </div>
      <div className={styles['form-field-input']}>
        <input 
          name={name}
          type="text" 
          autoComplete="off"
          style={style}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={e=>setInputBuffer(e.target.value)}
          onBlur={()=>onChange(inputValue)}
          value={inputValue !== null ? inputValue: ''}
          disabled={disabled}/>
      </div>
    </div>
  )
}

/*
  Renders Text Input Field with input props.
  inputProps for validation and value binding
*/
export const NumericInput = ({children, inputProps, maxLength, placeholder, numberLimit=null, decimalLimit=null, variant="flat"}) => {
  const { name, onChange, style, value } = inputProps;
  const [ inputValue, setInputValue ] = useState(value);
  const styleVartiants = {
    flat: 'form-field-input-number',
    default: 'form-field-input'
  }
  useEffect(()=>{setInputValue(value)},[value]);
  const setInputBuffer = (e) => {
    const string = e.target.value;
    const regexValidation = /^[0-9.]+$/;
    let invalidFlag = false;
    if(regexValidation.test(string) || string === null || string === ''){
      if(numberLimit){
        if(parseFloat(string) > numberLimit){
          invalidFlag = true;
        }
      }
      if(string){
        if(string.split('.').length-1<=1 && !invalidFlag){
          if(decimalLimit && string.split('.').length-1===1){
            if(string.split('.')[1].length>decimalLimit){
              console.log("invalid")
              invalidFlag=true;
            }
          }
        }
        if(string.split('.').length>2){
          invalidFlag=true;
        }
      } 
    } else { invalidFlag=true; }
    if(!invalidFlag){ setInputValue(string);}
  }
  return (
    <div className={styles['form-field']}>
      <div className={styles['form-field-label']}>
        { children }
      </div>
      <div className={styles[styleVartiants[variant]]}>
        <input 
          name={name}
          type="text"
          autoComplete="off"
          style={style}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={e=>setInputBuffer(e)}
          onBlur={()=>onChange(inputValue)}
          value={inputValue !== null ? inputValue: ''}/>
      </div>
    </div>
  )
}


/*
  Input Field Title Tooltip. Input title with tooltip.
*/
export const InputTitleTooltip = ({title, children, style}) => {
  return (
    <div className={styles['form-field']}>
      <div className={styles['form-field-label']}>
        <span className={styles['tooltip-input']} style={style}>
          { title }
          <OverlayTrigger overlay={<Tooltip style={{'font-size': '12px'}}>{children}</Tooltip>}>
            <img src={informationIcon}/>
          </OverlayTrigger>
        </span>
      </div>
    </div>
  )
}

/*
  Form Group Title Tooltip. Input title with tooltip.
*/
export const FormTitleTooltip = ({title, children, style}) => {
  return (
    <div className={styles['form-field']} style={{marginTop:'-10px'}}>
      <div className={styles['form-field-label']}>
        <span className={styles['tooltip-group']} style={style}>
          { title }
          <OverlayTrigger overlay={<Tooltip style={{'font-size': '12px'}}>{children}</Tooltip>}>
            <img src={informationIcon}/>
          </OverlayTrigger>
        </span>
      </div>
    </div>
  )
}

/*
  Currency Text Input Field with input props
  inputProps for validation and value binding
*/
export const CurrencyInput = ({children, inputProps, maxLength, placeholder, displayInWords=false ,noDecimal=false, decimalLimit=null}) => {
  const { name, onChange, style, value } = inputProps;
  const [ inputValue, setInputValue ] = useState(value);
  const [ amountInWords, setAmountInWords ] = useState('');
  useEffect(()=>{ setInputValue(addCommas(value))},[value]);
  const addCommas = (val) => {
    var parts = val.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.')
  }
  const transformAmount = (val) => {
    const regexValidation = noDecimal? /^[0-9,]+$/ : /^[0-9.,]+$/;
    if( displayInWords ){
      setAmountInWords(numToWords()(val));
    }
    if( val.match(regexValidation) || val === null || val === '' ){
      if(decimalLimit && val){
        if(val.split('.').length > 1){
          if(val.split('.').length>2){
            return inputValue;
          }
          if(val.split('.')[1].length>decimalLimit){
            return inputValue;
          }
        }
      }
      const nonSpaceValue = val== ''? null : val;
      const nonCommaValue = nonSpaceValue? nonSpaceValue.replace(/,/gi,''): '';
      const numString = addCommas(nonCommaValue);
      return numString;
    }
    return inputValue;
  }
  return (
    <>
      <div className={styles['form-field']}>
        <div className={styles['form-field-label']}>  
          { children }
        </div>
        <div className={styles['form-field-input']}>
          <input 
            name={name}
            type="text"
            autoComplete="off"
            style={style}
            maxLength={maxLength}
            placeholder={placeholder}
            className={styles['currency']}
            onChange={e=>setInputValue(transformAmount(e.target.value))}
            onBlur={()=>onChange(inputValue)}
            value={inputValue !== null ? inputValue: ''}/>
        </div>
      </div>
      { displayInWords &&
        <div className={styles['form-field']}>
          <div className={styles['form-field-input']}>
            <input
              type="text"
              autoComplete="off"
              style={{textAlign: 'right'}}
              placeholder="In Words"
              value={amountInWords}
              disabled
            />
          </div>
        </div>
      }
    </>
  )
}

/*
  Renders Radio Button Field with custom input props.
  inputProps fro validation and value binding
*/
export const RadioButton = ({ children, inputProps, data, othersOption, disabled, nameIndex=null, textFieldLarge}) => {
  const { name, value, style, onChange } = inputProps;
  const [ labelStyle, setLabelStyle ] = useState({});
  const [ inputValue, setInputValue ] = useState(value);
  const [ othersSelected, setOthersSelected ] = useState(false);

  useEffect(()=>{
    setInputValue(value);
  },[value])

  useEffect(()=>{
    if(style!=null){
      if(Object.keys(style).length!==0){setLabelStyle({color:'red'})}
    } else { setLabelStyle({}) }
  },[style])
  useEffect(()=>{
    if ( value !== null && value !== '' && othersOption ){
      const flag = data.some(i=>i.value.includes(value));
      setOthersSelected(!flag);
    }
  },[])
  const onChangeHandler = (v, other) => {
    setOthersSelected(other);
    setInputValue(v);
    onChange(v);
  }
  const renderOthersOption = () => {
    if(othersOption) {
      if(textFieldLarge){
        return (<>
          <label className={styles['option']}>
            <input type="radio" 
              name={name} 
              id="others" 
              checked={othersSelected}
              onChange={()=>onChangeHandler('',true)}/>
              <span style={labelStyle}>Others</span>
          </label>
          <div className={styles['large']}>
            <textarea
              type="text" 
              autoComplete="off"
              hidden={!othersSelected} 
              onChange={(e)=>setInputValue(e.target.value)} 
              value={inputValue}
              onBlur={()=>onChange(inputValue)}
              placeholder="Please specify"/>
          </div>
        </>)
      }
      // return (
      //   <label className={styles['option']}>
      //     <div className={styles['small']}>
      //       <input type="radio" 
      //         name={name} 
      //         id="others" 
      //         checked={othersSelected}
      //         onChange={()=>onChangeHandler('',true)}/>
      //         <span style={labelStyle}>Others</span>
      //       <input 
      //         type="text" 
      //         autoComplete="off"
      //         hidden={!othersSelected} 
      //         onChange={(e)=>setInputValue(e.target.value)} 
      //         value={inputValue}
      //         onBlur={()=>onChange(inputValue)}
      //         placeholder="Please specify"/>
      //     </div>
      //   </label>
      // )
    }}
    return (
      <div className={styles['form-field']}>
        <div className={styles['form-field-label']}>
          { children }
        </div>
        <div className={styles['form-field-radio']}>
          {
            data.map((data, index)=>{
              return (
                <div key={index}>
                  <label className={styles['option']}>
                  <input type="radio"
                    name={name + nameIndex}
                    autoComplete="off"
                    id={data['value']}
                    value={data['value']}
                    onChange={(e)=>onChangeHandler(e.target.value, false)}
                    style={style}
                    checked={inputValue === data['value']}
                    disabled={disabled}/>
                  <span style={labelStyle}>
                    { data['name'] }
                  </span>
                </label>
              </div>
            )
          })
        }
        { renderOthersOption() }
      </div>
    </div>
  )
}

export const CheckBoxes = ({ children, inputProps, values, othersOption, othersText, disabled=false }) => {
  const { name, value, style, onChange } = inputProps;
  const buildCheckListObj = list => {
    const ret = {};
    list.forEach(element => {
      ret[element['value']] = value.includes(element['value'])
    });
    ret['others'] = '';
    return ret;
  }

  const [ checkedList, setCheckedList ] = useState(buildCheckListObj(values));
  console.log("CHECKEDLIST:");
  console.log(checkedList);
  const [ labelStyle, setLabelStyle ] = useState({});
  useEffect(()=>{ 
    if(style != null){
      if(Object.keys(style).length!==0){
        setLabelStyle({color:'red'})
      } else { setLabelStyle({}) }
    } else { setLabelStyle({}) }
  },[style])
  useEffect(()=>{
    let checkArray = [];
    const keys = Object.keys(checkedList);
    for(var index in keys){
      if(checkedList[keys[index]]){ 
        if(keys[index] == 'others'){
          checkArray.push(checkedList[keys[index]])
        }
        else {
          checkArray.push(values[index]['value'])
        }
      }
    }
    onChange(checkArray);
  },[checkedList])

  const addToList = (e) => {
    const key = e.target.value;
    const status = e.target.checked;
    setCheckedList(prev=>({
      ...prev,
      [key]: status
    }))
  }
  const setOthersField = (e) => {
    const strValue = e.target.value;
    setCheckedList(prev=>({
      ...prev,
      others: strValue
    }))
  }
  const renderOthersOption = () => {
    return (
      <>
        <label className={styles['option']}>
          <div className={styles['others']}>
            Others: 
            <input
              type="text"
              onBlur={(e)=>{setOthersField(e)}}
              autoComplete="off"
              placeholder={othersText}/>
          </div>
        </label>
      </>
    )
  }
  return (
    <div className={styles['form-field']}>
      <div className={styles['form-field-label']}>
        { children }
      </div>
      <div className={styles['form-field-checkbox']}>
        {
          values.map((data, index)=>{
            return (
              <div key={name + '-' + index}>
                <label>
                  <input
                    type="checkbox"
                    checked={checkedList[data['value']]}
                    value={data['value']}
                    id={children+"-"+index}
                    onChange={(e)=>{addToList(e)}}
                    disabled={disabled}/>
                  <span className={styles['option']} style={labelStyle}>{ data['name'] }</span>
                </label>
              </div>
            )
          })
        }
        { othersOption && renderOthersOption() }
      </div>
    </div>
  )
}


export const RadioTextInput = ({ children, header, inputProps, data, noOptionIndexes}) => {
  const { name, value, style, onChange } = inputProps;
  const [ labelStyle, setLabelStyle ] = useState({});
  useEffect(()=>{
    if(style!=null){
      if(Object.keys(style).length!==0){setLabelStyle({color:'red'})}
    } else { setLabelStyle({}) }
  },[style])
  return (
    <div className={styles['form-field']}>
      <div className={styles['form-field-label']}>
        { header }
      </div>
      <div className={styles['form-field-radio']}>
        {
          data.map((data, index)=>{
            return (
              <div key={index} style={{marginBottom: '10px'}}>
                <label className={styles['option']}>
                  <div className={styles['others']}>
                    <input type="radio"
                      name={name}
                      id={data['value']}
                      checked={value== data['value']}
                      onChange={(e)=>onChange(e.target.value)}
                      value={data['value']}/>
                      <span style={labelStyle}>{data['name']}</span>
                    <div hidden={noOptionIndexes.includes(index)}>
                      <div style={ value === data['value']? {} : {pointerEvents: 'none', opacity: '0'} }> 
                        { children }
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            )
          })
        }
      </div>
    </div>
  )

}

export const DateInput = ({children, inputProps, min, maxLength, disabled=false}) => {
  const { name, onChange, style, value } = inputProps;
  const [ fieldStyle, setFieldStyle ] = useState();
  const [ inputValue, setInputValue ] = useState(value);
  const isDate = (date) => {
    return (new Date(date) !== "Invalid Date") && !isNaN(new Date(date));
  }
  useEffect(()=>{
    setInputValue(inputValue.replace(/\//g,'-'));
    if( value!==null && value!=='' && !isDate(value.replace(/\//g,'-'))){setFieldStyle({border: '1px solid red'});}
    else {setFieldStyle(style);}
  },[style, value])
  return (
    <div className={styles['form-field']}>
      <div className={styles['form-field-label']}>
        { children }
      </div>
      <div className={styles['form-field-input']}>
        <input 
          name={name}
          type="date" 
          style={fieldStyle}
          placeholder="yyyy/dd/mm"
          onChange={e=>setInputValue(e.target.value)}
          maxLength={maxLength}
          min={min}
          max="9999-01-01"
          disabled={disabled}
          onBlur={()=>onChange(inputValue.replace(/\//g,'-'))}
          value={inputValue !== null ? inputValue: ''}/>
      </div>
    </div>
  )
}

/*
  Renders Text Input Field with input props.
  inputProps for validation and value binding
*/
export const TextArea = ({children, inputProps, maxLength, placeholder, subtext, pattern=null}) => {
  const { name, onChange, style, value } = inputProps;
  const [ inputValue, setInputValue ] = useState(value);
  useEffect(()=>{setInputValue(value)},[value]);
  const setInputBuffer = (string) => {
    if(string && pattern){
      if(pattern.test(string)){setInputValue(string)};
    } else {
      setInputValue(string);
    }
  }
  return (
    <div className={styles['form-field']}>
      <div className={styles['form-field-label']}>
        { children }
      </div>
      <div className={styles['form-field-input']}>
        <textarea
          name={name}
          type="text" 
          autoComplete="off"
          style={style}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={e=>setInputBuffer(e.target.value)}
          onBlur={()=>onChange(inputValue)}
          value={inputValue !== null ? inputValue: ''}/>
        <div className={styles['subtext']}>
          { subtext }
        </div>
      </div>

    </div>
  )
}




